import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Grid,
  Snackbar,
  Switch,
  TextField,
  Typography,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { useApi } from "../../hooks/useApi";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";
import UnsavedChangesBar from "../shared/UnsavedChangesBar";

import LoadingComponent from "../../shared_modules/loadingComponent";

const Schedule = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const api = useApi();

  const [originalSchedule, setOriginalSchedule] = useState([]);
  const [schedule, setSchedule] = useState([]);

  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState(null); // { message, severity }

  const [showAlert, setShowAlert] = useState({
    show: false,
    message: "",
    type: "",
  });

  const hasChanges =
    JSON.stringify(schedule) !== JSON.stringify(originalSchedule);

  useUnsavedChangesGuard(hasChanges);

  const retrieveSchedule = async () => {
    setIsLoading(true);

    try {
      const response = await api("/tenants/schedule.php", { method: "GET" });
      setOriginalSchedule(response.data.hours);
      setSchedule(response.data.hours);
    } catch (err) {
      setShowAlert({ show: true, message: err.message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSchedule = async () => {
    setSaving(true);

    try {
      await api("/tenants/schedule.php", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ hours: schedule }),
      });

      setOriginalSchedule(schedule);
      setSnack({ message: "Shop hours saved.", severity: "success" });
    } catch (err) {
      setSnack({
        message: err.message || "Failed to save shop hours.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const discardChanges = () => setSchedule(originalSchedule);

  useEffect(() => {
    retrieveSchedule();
  }, []);

  const toggleDay = (index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      is_open: updated[index].is_open === 1 ? 0 : 1,
    };
    setSchedule(updated);
  };

  const changeOpenTime = (openTime, index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      open_time: openTime,
    };
    setSchedule(updated);
  };

  const changeCloseTime = (closeTime, index) => {
    const updated = [...schedule];
    updated[index] = {
      ...updated[index],
      close_time: closeTime,
    };
    setSchedule(updated);
  };

  const renderSchedule = () => {
    return (
      <Grid container>
        {schedule.map((day, index) => (
          <Box
            key={day.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              opacity: day.is_open ? "1" : "0.85",
              backgroundColor: day.is_open ? "offWhite" : "#f5f3ef",
              borderBottom:
                index !== schedule.length - 1 ? `0.1px solid` : undefined,
              borderColor: "customDivider",
              p: 2,
            }}
          >
            {/* Toggle + day name row */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Switch
                  checked={day.is_open === 1}
                  onChange={() => toggleDay(index)}
                />
                <Typography variant="subtitle1">{day.day_name}</Typography>
              </Box>

              {/* Desktop: time fields inline */}
              {day.is_open ? (
                <Box sx={{ display: { xs: "none", md: "flex" }, alignItems: "center", gap: 1 }}>
                  <TextField
                    variant="outlined"
                    placeholder="Open Time"
                    type="time"
                    value={day.open_time}
                    onChange={(e) => changeOpenTime(e.target.value, index)}
                    sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                  <Typography variant="body1">to</Typography>
                  <TextField
                    variant="outlined"
                    placeholder="Close Time"
                    type="time"
                    value={day.close_time}
                    onChange={(e) => changeCloseTime(e.target.value, index)}
                    sx={{ width: 150, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ fontStyle: "italic" }}>
                  Closed
                </Typography>
              )}
            </Box>

            {/* Mobile: time fields on their own row */}
            {!!day.is_open && (
              <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1, mt: 1.5, pl: 1 }}>
                <TextField
                  variant="outlined"
                  placeholder="Open Time"
                  type="time"
                  value={day.open_time}
                  onChange={(e) => changeOpenTime(e.target.value, index)}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
                <Typography variant="body1">to</Typography>
                <TextField
                  variant="outlined"
                  placeholder="Close Time"
                  type="time"
                  value={day.close_time}
                  onChange={(e) => changeCloseTime(e.target.value, index)}
                  sx={{ flex: 1, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                />
              </Box>
            )}
          </Box>
        ))}
      </Grid>
    );
  };

  return (
    <>
      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 3, md: 5 }, pt: 5, pb: 10 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant={isMobile ? "h4" : "h3"} sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Shop Hours
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Set when your shop accepts bookings
          </Typography>
        </Box>

        {showAlert.show === true && (
          <Alert
            sx={{ mb: 2 }}
            severity={showAlert.type}
            onClose={() => {
              setShowAlert({ show: false, message: "", type: "" });
            }}
          >
            {showAlert.message}
          </Alert>
        )}

        {isLoading ? (
          <LoadingComponent marginTop={10} />
        ) : schedule && schedule.length > 0 ? (
          <Box
            sx={{
              backgroundColor: "offWhite",
              p: 0,
              borderRadius: 2,
              border: `0.1px solid`,
              borderColor: "customDivider",
              width: "100%",
              overflow: "hidden",
            }}
          >
            {renderSchedule()}
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              No schedule yet.
            </Typography>
          </Box>
        )}

        <UnsavedChangesBar
          open={hasChanges && !isLoading}
          saving={saving}
          onDiscard={discardChanges}
          onSave={saveSchedule}
        />

        <Snackbar
          open={!!snack}
          autoHideDuration={2500}
          onClose={() => setSnack(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={snack?.severity || "success"}
            variant="filled"
            onClose={() => setSnack(null)}
            sx={{ minWidth: 200 }}
          >
            {snack?.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default Schedule;
